/**
 * Decide qué hacer con una venta cuyo envío falla: reintentar, cerrar si el servidor ya la tiene, o
 * darla por rechazada. Decide con `status` y `code`, nunca con el texto: el error de PostgREST es un
 * objeto plano y `String(error)` da «[object Object]».
 */

type ClaseDeFalla = 'reintentable' | 'definitivo' | 'ya-registrada';

/** Transitorio: el servidor no pudo ahora, pero la venta sigue siendo válida. */
const CODIGOS_TRANSITORIOS = new Set([
  'PGRST301', // JWT expirado — supabase-js refresca y el reintento pasa
  'PGRST302', // JWT inválido
  'PGRST002', // PostgREST sin caché de esquema todavía
  '57014', // statement_timeout
  '53300', // too_many_connections
  '40001', // serialization_failure
  '40P01', // deadlock_detected
  '08006', // connection_failure
  '08003', // connection_does_not_exist
]);

/** Definitivo: reintentar da exactamente el mismo resultado. */
const CODIGOS_DEFINITIVOS = new Set([
  'P0001', // raise exception: una regla de negocio nuestra
  'P0002', // no_data_found (lo usa registrar_tercero_publico)
  '42501', // RLS o permiso denegado
  '23502', // not_null_violation
  '23503', // foreign_key_violation
  '23514', // check_violation
  '22P02', // invalid_text_representation
  '22007', // invalid_datetime_format
  '42703', // undefined_column: el cliente está desactualizado
  'PGRST205', // tabla que no existe en el esquema
  'PGRST116', // se pidió una fila y llegaron varias
]);

interface FormaDeError {
  code?: string | null;
  status?: number;
  mensaje: string;
}

function formaDe(error: unknown): FormaDeError {
  const e = (error ?? {}) as { code?: string | null; status?: number; codigo?: string; mensajeCrudo?: string; message?: string };
  // Acepta tanto un `ErrorDeApi` (`codigo`, `mensajeCrudo`) como el objeto crudo de PostgREST (`code`, `message`).
  return { code: e.codigo ?? e.code ?? null, status: e.status, mensaje: e.mensajeCrudo ?? e.message ?? '' };
}

export function clasificarFalla(error: unknown): ClaseDeFalla {
  const { code, status, mensaje } = formaDe(error);

  // La venta ya existe en el servidor: el envío duplicado no es un problema, es el final feliz. Solo lo dice
  // el índice de idempotencia; otro duplicado es una venta que no entró, y no se tira.
  if (code === '23505') return mensaje.includes('comprobante_idempotente_unico') ? 'ya-registrada' : 'definitivo';

  // El fetch no llegó a salir o no volvió: sin red.
  if (status === 0) return 'reintentable';
  if (status === 408 || status === 429) return 'reintentable';
  if ((status ?? 0) >= 500) return 'reintentable';

  if (code && CODIGOS_TRANSITORIOS.has(code)) return 'reintentable';
  if (code && CODIGOS_DEFINITIVOS.has(code)) return 'definitivo';

  // 401/403 sin código reconocible suele ser la sesión a medio refrescar: se reintenta.
  if (status === 401 || status === 403) return code ? 'definitivo' : 'reintentable';

  // Lo que no reconocemos NO se tira. Guardar una venta que el servidor iba a rechazar cuesta que
  // el cajero la descarte a mano; borrar una que era buena cuesta la plata de un cliente.
  return 'reintentable';
}
